import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from 'src/context/auth/use-auth';
import { AuthHeading } from 'src/components/Layout';
import { Loader } from 'src/components/Loader';
import { Button } from '@/components/ui/button';
import EmailStepForm, { type IEmailStepForm } from 'src/components/Authentication/Login/EmailStepForm';
import PasswordStepForm, { type IPasswordStepForm } from 'src/components/Authentication/Login/PasswordStepForm';
import env from 'env';

type Stage = 'email' | 'checking' | 'password' | 'sso' | 'failed';

const LoginContainer = () => {
  const { signIn, signInWithSSO } = useAuth();
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [ssoProvider, setSsoProvider] = useState('');

  const startSsoSignIn = async (provider: string) => {
    setSsoProvider(provider);
    setStage('sso');

    try {
      await signInWithSSO(provider);
    } catch {
      setStage('failed');
    }
  };

  const onEmailSubmit = async ({ email }: IEmailStepForm) => {
    setEmail(email);
    setStage('checking');

    try {
      const response = await fetch(`${env.VITE_BACKEND_URL}/sso`, {
        method: 'POST',
        body: JSON.stringify({ email }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const { provider } = await response.json();
        await startSsoSignIn(provider);
      } else {
        setStage('password');
      }
    } catch {
      setStage('email');
      toast.error('Something went wrong. Please try again.');
    }
  };

  const onRetrySso = () => startSsoSignIn(ssoProvider);

  const onPasswordSubmit = async ({ password }: IPasswordStepForm) => {
    try {
      await signIn(email, password);
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message);
    }
  };

  if (stage === 'failed') {
    return (
      <div className="text-center">
        <AuthHeading title="Sign-in failed" description="Please try again or contact support." />
        <Button type="button" size="lg" variant="outline" className="w-full" onClick={onRetrySso}>
          Try again
        </Button>
      </div>
    );
  }

  if (stage === 'checking') {
    return (
      <div className="text-center">
        <AuthHeading title="Checking your account…" />
        <Loader className="h-auto mb-2" />
      </div>
    );
  }

  if (stage === 'sso') {
    return (
      <div className="text-center">
        <AuthHeading
          title="Redirecting to SSO provider…"
          description="You'll be returned to Codekeeper once you've signed in."
        />
        <Loader className="h-auto mb-2" />
      </div>
    );
  }

  if (stage === 'password') {
    return (
      <>
        <AuthHeading title="Enter your password" description="Enter your password to sign in." />
        <PasswordStepForm email={email} onSubmit={onPasswordSubmit} onBack={() => setStage('email')} />
      </>
    );
  }

  return (
    <>
      <AuthHeading title="Sign in" description="Welcome! Sign in to your Codekeeper account." />
      <EmailStepForm defaultEmail={email} onSubmit={onEmailSubmit} />
    </>
  );
};

export default LoginContainer;
